import React, { useState } from "react";
import { Box, Typography, Chip, Button, CircularProgress } from "@mui/material";
import { Campaign as CampaignIcon } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
  useBulkAddItemsToCampaign,
  useCampaignImportCandidates,
} from "../../../../../client/hooks/products/promotions.mutations";
import CampaignImportData from "../CampaignImportData";
import * as XLSX from "xlsx";

export default function CampaignDetailsHeader({ campaign, getStatusColor }) {
  const { mutate: exportCandidates } = useCampaignImportCandidates();
  const { mutate: importCandidates } = useBulkAddItemsToCampaign();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function handleExportData() {
    setIsLoading(true);
    exportCandidates(
      { campaign },
      {
        onSuccess: (blob) => {
          const fileName = `${campaign.name !== undefined ? campaign.name : "Campanha sem nome"}.xls`;
          downloadBlob(blob, fileName);
        },
        onSettled: () => {
          setIsLoading(false);
        },
      }
    );
  }
  function handleImporData() {
    setImportDialogOpen(true);
  }

  const handleFileSelected = async (file) => {
    setIsLoading(true);
    try {
      const data = await file.arrayBuffer(); // lê como binário
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Monta estrutura final
      const payload = {
        promotion_type: campaign.type,
        items: json.map((row) => ({
          item_id: String(row["Item ID"]).trim(),
          deal_price: Number(
            row["Suggested Discounted Price"] ||
              row["deal_price"] ||
              row["preco"] ||
              row["preço"] ||
              row["price"] ||
              row["Price"] ||
              0
          ),
          offer_id: row["Offer Id"]?.toString().trim() || null,
        })),
      };
      importCandidates({ promotionId: campaign.id, data: payload });
      // Aqui você pode fazer o POST com `api.post("/rota", payload)`
    } catch (err) {
      console.error("Erro ao processar o arquivo:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CampaignIcon sx={{ color: "#3483fa", fontSize: 28 }} />
        <Typography variant="h6" sx={{ color: "#3483fa", fontWeight: 600 }}>
          Detalhes da Campanha
        </Typography>
        <Chip
          label={campaign.name || "Sem nome"}
          size="small"
          sx={{
            fontWeight: 500,
            borderRadius: 2,
            bgcolor: "grey.100",
            color: "grey.800",
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Chip
          label={campaign.status || "Desconhecido"}
          color={getStatusColor(campaign.status)}
          size="small"
          sx={{ fontWeight: 500, borderRadius: 2 }}
        />{" "}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="outlined"
            disabled={isLoading}
            size="medium"
            onClick={() => handleExportData()}
            startIcon={
              isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            sx={{
              borderColor: "#3483fa",
              color: "#3483fa",
              bgcolor: "rgba(52, 131, 250, 0.04)",
              "&:hover": {
                bgcolor: "rgba(52, 131, 250, 0.08)",
                borderColor: "#2968c8",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(52, 131, 250, 0.2)",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#999",
                bgcolor: "rgba(0,0,0,0.02)",
              },
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1,
              borderRadius: 2,
              minWidth: 140,
              transition: "all 0.2s ease",
            }}
          >
            Exportar campanha
          </Button>

          <Button
            variant="contained"
            size="medium"
            onClick={() => handleImporData()}
            startIcon={<FileUploadIcon />}
            sx={{
              bgcolor: "#00a650",
              color: "white",
              "&:hover": {
                bgcolor: "#008a43",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 166, 80, 0.3)",
              },
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1,
              borderRadius: 2,
              minWidth: 140,
              transition: "all 0.2s ease",
            }}
          >
            Aderir em Massa
          </Button>
        </Box>
      </Box>{" "}
      <CampaignImportData
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onFileSelected={handleFileSelected}
        campaign={campaign}
      />
    </Box>
  );
}
